/**
 * Active-client state for the Manager app. When a client is "activated", the
 * chat scopes its questions to that client and the Timeline page shows that
 * client's timeline. Persisted to localStorage so it survives reloads.
 */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export type ActiveClient = { id: string; name: string } | null;

const KEY = 'ma-active-client';

const ActiveClientContext = createContext<{
  activeClient: ActiveClient;
  setActiveClient: (c: ActiveClient) => void;
}>({ activeClient: null, setActiveClient: () => {} });

export function ActiveClientProvider({ children }: { children: ReactNode }) {
  const [activeClient, setActiveClientState] = useState<ActiveClient>(() => {
    try { const raw = localStorage.getItem(KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
  });

  const setActiveClient = (c: ActiveClient) => {
    setActiveClientState(c);
    try {
      if (c) localStorage.setItem(KEY, JSON.stringify(c));
      else localStorage.removeItem(KEY);
    } catch { /* ignore */ }
  };

  // Keep multiple tabs / the picker in sync if changed elsewhere.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== KEY) return;
      try { setActiveClientState(e.newValue ? JSON.parse(e.newValue) : null); } catch { /* ignore */ }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <ActiveClientContext.Provider value={{ activeClient, setActiveClient }}>
      {children}
    </ActiveClientContext.Provider>
  );
}

export function useActiveClient() { return useContext(ActiveClientContext); }
